import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
import { SHAPE_BOUNDS, UNIT } from './patternShapesConstants';
var PICKER_OFFSET = 6; // px outset on each side so buttons frame the shape with breathing room
var isPatternOutline = function (d) {
    return !!d.isOutline || !!d.isStaticOutline || d.shapeType === 'outline';
};
// Same numbering logic as PatternShapesManipulative: 1-based index among all shapes
// sharing the same shapeType + color across the entire manipulative array.
var getShapeNumber = function (allManipulative, shapeId, shapeType, color) {
    var peers = allManipulative.filter(function (m) {
        var d = m.data;
        return (d === null || d === void 0 ? void 0 : d.type) === 'patternShapes' && d.shapeType === shapeType && String(d.color) === color;
    });
    return peers.findIndex(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === shapeId; }) + 1;
};
var buildCandidates = function (manipulative, focusedShape) {
    var _a, _b, _c, _d, _e;
    var others = manipulative
        .filter(function (m) {
        var _a;
        return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes' &&
            m.data.id !== focusedShape.id &&
            !isPatternOutline(m.data) &&
            // Exclude other members of the focused shape's own group
            !(focusedShape.groupId && m.data.groupId === focusedShape.groupId);
    })
        .map(function (m) { return m.data; });
    var groupMap = new Map();
    var standalones = [];
    for (var _i = 0, others_1 = others; _i < others_1.length; _i++) {
        var shape = others_1[_i];
        if (shape.groupId) {
            var arr = (_a = groupMap.get(shape.groupId)) !== null && _a !== void 0 ? _a : [];
            arr.push(shape);
            groupMap.set(shape.groupId, arr);
        }
        else {
            standalones.push(shape);
        }
    }
    // Ordered list of all group IDs on canvas (matches context-menu numbering)
    var allGroupIds = [];
    manipulative.forEach(function (m) {
        var _a, _b, _c;
        var gId = (_a = m.data) === null || _a === void 0 ? void 0 : _a.groupId;
        if (gId && ((_c = (_b = m.data) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.startsWith('patternShapes_')) && !allGroupIds.includes(gId)) {
            allGroupIds.push(gId);
        }
    });
    var candidates = [];
    // Groups first
    groupMap.forEach(function (members, groupId) {
        var _a;
        candidates.push({
            kind: 'group',
            groupId: groupId,
            groupNumber: allGroupIds.indexOf(groupId) + 1,
            shapeIds: members.map(function (m) { return m.id; }),
            centers: members.map(function (m) { return m.center; }),
            scale: (_a = members[0].scale) !== null && _a !== void 0 ? _a : 1,
            shapeTypes: members.map(function (m) { return m.shapeType; }),
            colors: members.map(function (m) { var _a; return String((_a = m.color) !== null && _a !== void 0 ? _a : ''); }),
            shapeNumbers: members.map(function (m) { var _a; return getShapeNumber(manipulative, m.id, m.shapeType, String((_a = m.color) !== null && _a !== void 0 ? _a : '')); }),
            rotations: members.map(function (m) { var _a; return (_a = m.rotation) !== null && _a !== void 0 ? _a : 0; }),
        });
    });
    // Standalones after
    for (var _f = 0, standalones_1 = standalones; _f < standalones_1.length; _f++) {
        var shape = standalones_1[_f];
        candidates.push({
            kind: 'standalone',
            shapeId: shape.id,
            center: shape.center,
            scale: (_b = shape.scale) !== null && _b !== void 0 ? _b : 1,
            shapeType: shape.shapeType,
            color: String((_c = shape.color) !== null && _c !== void 0 ? _c : ''),
            shapeNumber: getShapeNumber(manipulative, shape.id, shape.shapeType, String((_d = shape.color) !== null && _d !== void 0 ? _d : '')),
            rotation: (_e = shape.rotation) !== null && _e !== void 0 ? _e : 0,
        });
    }
    return candidates;
};
// Returns all button IDs (data-btn-id values) for a candidate
var candidateBtnIds = function (c) {
    if (c.kind === 'standalone')
        return [c.shapeId];
    return c.shapeIds;
};
var PatternShapeLinkPicker = function () {
    var _a;
    var t = useTranslation().t;
    var manipulative = useContext(DataContext).manipulative;
    var _b = useState(null), focusedShapeId = _b[0], setFocusedShapeId = _b[1];
    // Index into candidates[] — which candidate is keyboard-focused
    var _c = useState(0), activeIdx = _c[0], setActiveIdx = _c[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    // Open: listen for custom event
    useEffect(function () {
        var onOpen = function (data) {
            returnFocusIdRef.current = data.focusedShapeId;
            setFocusedShapeId(data.focusedShapeId);
            setActiveIdx(0);
            announce(t('text.patternPickerOpened'));
        };
        customEventHandler.addEventListener('openPatternShapeLinkPicker', onOpen);
        return function () { return customEventHandler.removeEventListener('openPatternShapeLinkPicker', onOpen); };
    }, [t]);
    // Derive focused shape and candidates on every render (handles source-deleted case)
    var focusedItem = focusedShapeId
        ? manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === focusedShapeId; })
        : undefined;
    var focusedShape = ((_a = focusedItem === null || focusedItem === void 0 ? void 0 : focusedItem.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes'
        ? focusedItem.data
        : null;
    var candidates = focusedShape ? buildCandidates(manipulative, focusedShape) : [];
    // Auto-close guard: source deleted or no valid candidates
    useEffect(function () {
        if (focusedShapeId && (!focusedShape || candidates.length === 0)) {
            setFocusedShapeId(null);
        }
    }, [focusedShapeId, focusedShape, candidates.length]);
    // Click-outside: close picker but let event propagate (enables PS drag)
    useEffect(function () {
        if (!focusedShapeId)
            return;
        var handleOutside = function (e) {
            var _a;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))
                return;
            setFocusedShapeId(null);
        };
        window.addEventListener('pointerdown', handleOutside, { capture: true });
        return function () { return window.removeEventListener('pointerdown', handleOutside, { capture: true }); };
    }, [focusedShapeId]);
    // Focus first candidate button when picker opens.
    // Delay > 50ms to win over closeMenu's setTimeout focus-restore timer.
    useEffect(function () {
        if (!focusedShapeId)
            return;
        var id = setTimeout(function () { var _a; return (_a = firstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        return function () { return clearTimeout(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedShapeId]);
    if (!focusedShapeId || !focusedShape || candidates.length === 0)
        return null;
    var closePicker = function () {
        setFocusedShapeId(null);
        var id = returnFocusIdRef.current;
        if (id) {
            requestAnimationFrame(function () {
                var el = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                el === null || el === void 0 ? void 0 : el.focus();
            });
        }
    };
    var onSelect = function (candidate) {
        var _a;
        // When the source shape is already grouped, pass its groupId so all members are merged
        var sourceId = (_a = focusedShape === null || focusedShape === void 0 ? void 0 : focusedShape.groupId) !== null && _a !== void 0 ? _a : focusedShapeId;
        var diceIds = candidate.kind === 'standalone'
            ? [sourceId, candidate.shapeId]
            : [candidate.groupId, sourceId];
        customEventHandler.dispatchEvent('bottomPanel', {
            id: 'groupDiceFromMenu',
            panel: 'bottomPanel',
            diceIds: diceIds,
        });
        setFocusedShapeId(null);
    };
    // Keyboard navigation: move between candidates (not individual buttons within a group)
    var moveCandidateFocus = function (delta) {
        var next = (activeIdx + delta + candidates.length) % candidates.length;
        setActiveIdx(next);
        var cand = candidates[next];
        var targetId = cand.kind === 'standalone' ? cand.shapeId : cand.shapeIds[0];
        requestAnimationFrame(function () {
            var _a;
            var btn = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("button.psLinkPicker__target[data-btn-id=\"".concat(targetId, "\"]"));
            btn === null || btn === void 0 ? void 0 : btn.focus();
        });
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePicker();
            return;
        }
        if (e.key === 'ArrowRight' ||
            e.key === 'ArrowDown' ||
            (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(1);
        }
        else if (e.key === 'ArrowLeft' ||
            e.key === 'ArrowUp' ||
            (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(-1);
        }
    };
    var getCandidateIdx = function (btnId) {
        return candidates.findIndex(function (c) { return candidateBtnIds(c).includes(btnId); });
    };
    var onBtnFocus = function (btnId) {
        var idx = getCandidateIdx(btnId);
        if (idx !== -1)
            setActiveIdx(idx);
    };
    var btnCounter = 0;
    return (_jsx("div", { ref: containerRef, className: "psLinkPicker", role: "dialog", "aria-modal": "true", "aria-label": t('text.patternPickerLabel'), onKeyDown: onContainerKeyDown, children: candidates.map(function (cand, candIdx) {
            var _a;
            var isActive = candIdx === activeIdx;
            var ids = candidateBtnIds(cand);
            var centers = cand.kind === 'standalone' ? [cand.center] : cand.centers;
            var shapeTypes = cand.kind === 'standalone' ? [cand.shapeType] : cand.shapeTypes;
            var colors = cand.kind === 'standalone' ? [cand.color] : cand.colors;
            var shapeNumbers = cand.kind === 'standalone' ? [cand.shapeNumber] : cand.shapeNumbers;
            var rotations = cand.kind === 'standalone' ? [cand.rotation] : cand.rotations;
            var scale = (_a = cand.scale) !== null && _a !== void 0 ? _a : 1;
            // For group candidates: only the FIRST button is SR-visible (counts as 1 dialog item).
            // The remaining buttons over other shapes in the group are aria-hidden click targets.
            return ids.map(function (shapeId, btnIdx) {
                var _a, _b, _c, _d, _e, _f;
                var isFirstOverall = btnCounter === 0;
                var isFirstInCand = btnIdx === 0;
                btnCounter++;
                var center = (_a = centers[btnIdx]) !== null && _a !== void 0 ? _a : centers[0];
                var st = (_b = shapeTypes[btnIdx]) !== null && _b !== void 0 ? _b : shapeTypes[0];
                var color = (_c = colors[btnIdx]) !== null && _c !== void 0 ? _c : colors[0];
                var num = (_d = shapeNumbers[btnIdx]) !== null && _d !== void 0 ? _d : 1;
                var rot = (_e = rotations[btnIdx]) !== null && _e !== void 0 ? _e : 0;
                var bounds = (_f = SHAPE_BOUNDS[st]) !== null && _f !== void 0 ? _f : { w: UNIT, h: UNIT };
                var w = bounds.w * scale;
                var h = bounds.h * scale;
                // CSS scale(s) is applied from the center of the layout box (bounds.w × bounds.h),
                // so the visual top-left shifts inward by (bounds - scaled) / 2 when scale ≠ 1.
                var visualLeft = center.x + (bounds.w - w) / 2;
                var visualTop = center.y + (bounds.h - h) / 2;
                // Only the first button in each candidate gets SR attributes.
                // For group candidates, secondary buttons are aria-hidden so SR counts the group as 1 item.
                var isSrVisible = isFirstInCand;
                var ariaLabel = isSrVisible
                    ? cand.kind === 'standalone'
                        ? t('text.patternPickerShapeTarget', {
                            shapeType: t("text.".concat(st), { defaultValue: st }),
                            color: t("text.color_".concat(color), { defaultValue: color }),
                            number: num,
                        })
                        : t('text.patternPickerGroupTarget', {
                            number: cand.groupNumber,
                            count: cand.shapeIds.length,
                            shapes: cand.shapeTypes.map(function (sType, i) {
                                var _a, _b, _c;
                                return t('text.patternPickerShapeTarget', {
                                    shapeType: t("text.".concat(sType), { defaultValue: sType }),
                                    color: t("text.color_".concat((_a = cand.colors[i]) !== null && _a !== void 0 ? _a : ''), { defaultValue: (_b = cand.colors[i]) !== null && _b !== void 0 ? _b : '' }),
                                    number: (_c = cand.shapeNumbers[i]) !== null && _c !== void 0 ? _c : 1,
                                });
                            }).join(', '),
                        })
                    : undefined;
                return (_jsx("button", { ref: isFirstOverall ? firstBtnRef : undefined, type: "button", className: "psLinkPicker__target".concat(isActive ? ' psLinkPicker__target--active' : ''), "data-btn-id": shapeId, "data-cand-idx": candIdx, tabIndex: isSrVisible ? 0 : -1, "aria-hidden": isSrVisible ? undefined : true, style: {
                        position: 'absolute',
                        left: visualLeft - PICKER_OFFSET,
                        top: visualTop - PICKER_OFFSET,
                        width: w + PICKER_OFFSET * 2,
                        height: h + PICKER_OFFSET * 2,
                        transform: rot !== 0 ? "rotate(".concat(rot, "deg)") : undefined,
                        transformOrigin: 'center',
                        zIndex: 99999,
                    }, "aria-label": ariaLabel, "aria-roledescription": isSrVisible && cand.kind === 'standalone' ? t('text.patternShape') : undefined, onFocus: function () { return isSrVisible && onBtnFocus(shapeId); }, onClick: function () { return onSelect(cand); } }, "".concat(shapeId, "-").concat(cand.kind)));
            });
        }) }));
};
export default PatternShapeLinkPicker;
